/**
 * What the Optimization tab shows BEFORE there is an agent to talk to.
 *
 * The optimization agent reasons over a finished optimizer run, so unlike the
 * bulk editor it cannot open a session on arrival — there may be no run yet, the
 * campaign may not be live, or a run may be in flight. This renders that gap as
 * the sidebar's `fallbackMessage`, which is the slot AgentSidebar already has for
 * "no session here", so no new surface is introduced.
 *
 * The Run button is deliberate. An optimizer run pulls the campaign's live
 * delivery data, costs money, and takes minutes — firing it because someone
 * clicked a tab is not a thing to do on the user's behalf. If a completed run
 * already exists the hook reuses it and this panel never appears; otherwise the
 * user asks for one.
 */

import { optimizeAnalysesEnabledFor } from '../../../utils/optimizeCampaignRun';
import { OPT_PHASE } from '../../../hooks/useOptimizationSession';

export function OptimizationRunGate({
  phase,
  context,
  runError,
  platformLabel,
  // Set when the HOST has switched the hook off, so nothing will ever load —
  // demo mode is the case that exists. Without it the panel sits on the IDLE
  // spinner forever, which reads as a hang rather than as "not here".
  disabledReason = null,
  // Optional escape hatch for the one ineligibility that is a missing FILE rather
  // than a missing capability — see the INELIGIBLE branch.
  onUploadRun = null,
}) {
  const fullAnalysis = optimizeAnalysesEnabledFor(context?.destination);
  if (disabledReason) {
    return (
      <>
        <strong>Optimization isn&apos;t available here.</strong>
        <div style={{ marginTop: 4 }}>{disabledReason}</div>
      </>
    );
  }

  if (phase === OPT_PHASE.LOADING || phase === OPT_PHASE.IDLE) {
    return (
      <>
        <div style={{ marginBottom: 8 }}>Checking this campaign for analysis reports…</div>
        <div className="clw-ai-typing">
          <span className="clw-ai-dot" /><span className="clw-ai-dot" /><span className="clw-ai-dot" />
        </div>
      </>
    );
  }

  if (phase === OPT_PHASE.INELIGIBLE) {
    // MadHive is ineligible only because its run needs a reporting-metrics file
    // uploaded first. A host that can host that upload passes onUploadRun, and the
    // user gets the real action instead of a dead end.
    const canUpload = context?.needsReportingMetrics && onUploadRun;
    return (
      <>
        <strong>{canUpload ? 'This run needs a file first.' : 'Optimization isn\u2019t available here.'}</strong>
        <div style={{ marginTop: 4 }}>
          {context?.reason || 'This campaign cannot be optimized.'}
        </div>
        {canUpload ? (
          <button type="button" className="clw-bulk-confirm" style={{ marginTop: 10 }} onClick={onUploadRun}>
            Upload metrics &amp; run
          </button>
        ) : (
          <div style={{ marginTop: 6, color: 'var(--g4)', fontSize: 11.5 }}>
            The <strong>Bulk edit</strong> tab still works — it changes the plan directly.
          </div>
        )}
      </>
    );
  }

  if (phase === OPT_PHASE.RUNNING) {
    return (
      <>
        <div style={{ marginBottom: 8 }}>
          {fullAnalysis ? 'Running diagnosis, performance, and budget pacing' : 'Running the optimizer'}
          {' '}on the live {platformLabel} campaign. This usually takes a few minutes — you can leave this tab and come back.
          {runError && <div style={{ marginTop: 8 }}>{runError}</div>}
        </div>
        <div className="clw-ai-typing">
          <span className="clw-ai-dot" /><span className="clw-ai-dot" /><span className="clw-ai-dot" />
        </div>
      </>
    );
  }

  if (phase === OPT_PHASE.ERROR) {
    return (
      <>
        <strong style={{ color: 'var(--red)' }}>{fullAnalysis ? 'Full analysis incomplete.' : 'Optimization failed.'}</strong>
        <div style={{ marginTop: 4 }}>{runError || 'The optimizer run did not complete.'}</div>
        <div style={{ marginTop: 6, color: 'var(--g4)', fontSize: 11.5 }}>
          Adjust the window above and run again.
        </div>
      </>
    );
  }

  // NO_RUN — eligible, nothing to read yet.
  //
  // One line. The window, the focus box and the Run button all live in
  // OptimizationRunBar directly above this, where they stay available for the
  // whole life of the tab — including after a run loads and this gate stops
  // rendering entirely. Duplicating them here would mean two sets of controls
  // that can disagree about what is about to run.
  return <>{fullAnalysis
    ? 'No full analysis yet — set a window above and run diagnosis, performance, and budget pacing.'
    : 'No optimizer run yet for this campaign — set a window above and run one.'}</>;
}

export default OptimizationRunGate;
